import type { WorkspaceIntegrationAPI } from '@common/api/authService';
import type { ConnectionData } from './ConnectionCard';

type KlaviyoUnavailableReason = 'disabled' | 'suspended' | 'trial_expired';

export interface IntegrationAvailability {
    isUnavailable: boolean;
    reason: KlaviyoUnavailableReason | null;
    status: ConnectionData['status'];
    label: string;
    message: string;
}

export const AVAILABLE_INTEGRATION_STATUS: IntegrationAvailability = {
    isUnavailable: false,
    reason: null,
    status: 'connected',
    label: 'Connected',
    message: '',
};

const DEFAULT_KLAVIYO_UNAVAILABLE_MESSAGE = 'Klaviyo reports this account is unavailable. Re-enable the account in Klaviyo, then reconnect or refresh Bustly.';

const STATUS_RULES: Array<{
    reason: KlaviyoUnavailableReason;
    status: ConnectionData['status'];
    label: string;
    pattern: RegExp;
    fallbackMessage: string;
}> = [
    {
        reason: 'trial_expired',
        status: 'trial_expired',
        label: 'Trial expired',
        pattern: /trial[\s_-]*(?:expired|ended)|(?:free|email|support).*trial.*(?:expired|ended)/i,
        fallbackMessage: 'Klaviyo account access appears to have expired. Upgrade or re-enable the account in Klaviyo, then refresh Bustly.',
    },
    {
        reason: 'suspended',
        status: 'suspended',
        label: 'Suspended',
        pattern: /suspend/i,
        fallbackMessage: 'Klaviyo reports this account is suspended. Resolve the account status in Klaviyo, then refresh Bustly.',
    },
    {
        reason: 'disabled',
        status: 'disabled',
        label: 'Disabled',
        pattern: /disable|deactivat|inactive/i,
        fallbackMessage: DEFAULT_KLAVIYO_UNAVAILABLE_MESSAGE,
    },
];

const asRecord = (value: unknown): Record<string, unknown> => (
    value && typeof value === 'object' ? value as Record<string, unknown> : {}
);

const addTextCandidate = (candidates: string[], value: unknown) => {
    if (typeof value === 'string' && value.trim()) {
        candidates.push(value.trim());
    }
};

const collectKlaviyoStatusCandidates = (integration: WorkspaceIntegrationAPI | null | undefined) => {
    const candidates: string[] = [];
    const integrationRecord = asRecord(integration);
    const profile = asRecord(integration?.profile_data);

    ['status', 'account_status', 'mapping_status', 'message', 'error_message', 'status_message'].forEach((key) => {
        addTextCandidate(candidates, profile[key]);
    });
    ['message', 'eligibilityMessage'].forEach((key) => {
        addTextCandidate(candidates, integrationRecord[key]);
    });

    (integration?.mappings || []).forEach((mapping) => {
        const mappingRecord = asRecord(mapping);
        ['status', 'account_status', 'mapping_status', 'message', 'error_message', 'status_message'].forEach((key) => {
            addTextCandidate(candidates, mappingRecord[key]);
        });
    });

    return candidates;
};

export const resolveKlaviyoIntegrationAvailability = (
    integration: WorkspaceIntegrationAPI | null | undefined
): IntegrationAvailability => {
    const candidates = collectKlaviyoStatusCandidates(integration);
    const matchedRule = STATUS_RULES.find(rule => candidates.some(candidate => rule.pattern.test(candidate)));

    if (!matchedRule) {
        return AVAILABLE_INTEGRATION_STATUS;
    }

    const matchingMessage = candidates.find(candidate => matchedRule.pattern.test(candidate));

    return {
        isUnavailable: true,
        reason: matchedRule.reason,
        status: matchedRule.status,
        label: matchedRule.label,
        message: matchingMessage || matchedRule.fallbackMessage,
    };
};

export const isIntegrationConnectionActive = (connection: Pick<ConnectionData, 'status'>) => (
    connection.status === 'connected' || connection.status === 'active'
);

export const getIntegrationConnectionStatusMeta = (connection: Pick<ConnectionData, 'status' | 'statusLabel'>) => {
    if (isIntegrationConnectionActive(connection)) {
        return {
            label: 'Connected',
            className: 'bg-green-50 text-green-700 ring-green-600/20',
            dotClassName: 'bg-green-500',
        };
    }

    if (connection.status === 'error') {
        return {
            label: 'Error',
            className: 'bg-red-50 text-red-700 ring-red-600/20',
            dotClassName: 'bg-red-500',
        };
    }

    if (connection.status === 'disabled' || connection.status === 'suspended' || connection.status === 'trial_expired') {
        return {
            label: connection.statusLabel || 'Attention needed',
            className: 'bg-amber-50 text-amber-800 ring-amber-500/25',
            dotClassName: 'bg-amber-500',
        };
    }

    return {
        label: 'Disconnected',
        className: 'bg-gray-50 text-gray-500 ring-gray-200/50',
        dotClassName: 'bg-gray-400',
    };
};
